/**
 * VerifyEmail Store - State management for VerifyEmail.tsx page
 *
 * Related page: frontend/src/pages/VerifyEmail.tsx
 *
 * This store demonstrates the "form with validation" pattern:
 * - verifyEmailFormAtom: form data (otp, isSubmitting, isResending)
 * - verifyEmailFormErrorsAtom: null before first submit, then contains errors
 * - Once submitted, errors update in real-time as user types
 */

import { atom } from 'jotai'
import { authClient } from '../client'
import { FormError } from 'txom-frontend-libs'
import { resetQueryClient } from '../../queryClient'

// ============================================================================
// FORM STATE
// ============================================================================

const initialVerifyEmailForm = {
  otp: '',
  isSubmitting: false,
  isResending: false,
}

type VerifyEmailForm = typeof initialVerifyEmailForm

export const verifyEmailFormAtom = atom(initialVerifyEmailForm)

// Success states
export const verifyEmailSuccessAtom = atom(false)
export const verifyEmailResendSuccessAtom = atom(false)

// ============================================================================
// FORM ERRORS
// - null: form has never been submitted, don't show errors
// - object: form has been submitted, show errors and validate in real-time
// ============================================================================

type VerifyEmailFormErrors = {
  form?: string // General form-level error (e.g., "Verification failed")
  otp?: string
}

export const verifyEmailFormErrorsAtom = atom<VerifyEmailFormErrors | null>(null)

// ============================================================================
// VALIDATION
// ============================================================================

function hasFieldErrors(errors: VerifyEmailFormErrors): boolean {
  const { form: _form, ...fieldErrors } = errors
  return Object.keys(fieldErrors).length > 0
}

const validateVerifyEmailFormAtom = atom(null, (get, set) => {
  const form = get(verifyEmailFormAtom)
  const errors: VerifyEmailFormErrors = {}

  if (!form.otp) {
    errors.otp = 'Verification code is required'
  } else if (form.otp.length !== 6) {
    errors.otp = 'Please enter the 6-digit code'
  }

  set(verifyEmailFormErrorsAtom, errors)
  return hasFieldErrors(errors)
})

const setVerifyEmailFormErrorAtom = atom(null, (_get, set, err: unknown, fallback: string) => {
  const message = err instanceof FormError ? err.message : (err instanceof Error ? err.message || fallback : fallback)
  set(verifyEmailFormErrorsAtom, (current) => ({ ...(current ?? {}), form: message }))
})

// ============================================================================
// FORM ACTIONS
// ============================================================================

export const updateVerifyEmailFormAtom = atom(null, (get, set, updates: Partial<VerifyEmailForm>) => {
  const current = get(verifyEmailFormAtom)
  set(verifyEmailFormAtom, { ...current, ...updates })

  // If form has been submitted before (errors !== null), validate in real-time
  if (get(verifyEmailFormErrorsAtom) !== null) {
    set(validateVerifyEmailFormAtom)
  }
})

export const submitVerifyEmailAtom = atom(null, async (get, set, email: string) => {
  const form = get(verifyEmailFormAtom)

  // Validate and enable real-time validation mode
  if (set(validateVerifyEmailFormAtom)) {
    throw new FormError('Please fix the errors above')
  }

  set(verifyEmailFormAtom, { ...form, isSubmitting: true })

  try {
    const res = await authClient.emailOtp.verifyEmail({ email, otp: form.otp })
    if (res.error) {
      throw new FormError(res.error.message || 'Verification failed')
    }
    set(verifyEmailSuccessAtom, true)
    // Clear query cache to refresh current user state
    set(resetQueryClient)
  } catch (err) {
    set(setVerifyEmailFormErrorAtom, err, 'Verification failed')
    throw err
  } finally {
    set(verifyEmailFormAtom, (current) => ({ ...current, isSubmitting: false }))
  }
})

export const resendVerificationOtpAtom = atom(null, async (get, set, email: string) => {
  const form = get(verifyEmailFormAtom)
  if (form.isResending) return

  set(verifyEmailFormAtom, { ...form, isResending: true })
  set(verifyEmailResendSuccessAtom, false)
  // Clear form-level error when resending
  set(verifyEmailFormErrorsAtom, (current) => current ? { ...current, form: undefined } : null)

  try {
    const res = await authClient.emailOtp.sendVerificationOtp({
      email,
      type: 'email-verification',
    })
    if (res.error) {
      throw new FormError(res.error.message || 'Failed to resend code')
    }
    set(verifyEmailResendSuccessAtom, true)
  } catch (err) {
    set(setVerifyEmailFormErrorAtom, err, 'Failed to resend code')
    throw err
  } finally {
    set(verifyEmailFormAtom, (current) => ({ ...current, isResending: false }))
  }
})

// ============================================================================
// ROUTE LOADERS
// ============================================================================

type Store = ReturnType<typeof import('jotai').createStore>

export async function loader(_store: Store) {
  return null
}
